import React, { FunctionComponent, useState } from 'react';
import { useFormikContext } from 'formik';
import { Form } from 'antd';

import { ValidationErrorMessages } from '@jetslash/market-frontend-shared-core/src/utilities/form/types';
import { parseErrorMessagesFromResponse } from '@jetslash/market-frontend-shared-core/src/utilities/form/formErrorsFromErrorResponse';
import useLoadingContext from '@jetslash/market-frontend-shared-core/src/contexts/LoadingContextProvider/useLoadingContext';

import FormErrorDisplay from '@/components/forms/FormErrorDisplay';
import FormContainer from '@/components/forms/FormContainer';
import Reporty from '@/utility/errorHandling/Reporty';

// interface IProps extends FormikConfig<any> {}

interface IEasyFormBag {
  onClickSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  submitDisabled: boolean;
  inputDisabled: boolean;
}

interface IProps {
  children: (IEasyFormBag) => React.ReactNode;
  // Keeps loading indicator up even after request succeeds. Useful for when we redirect on success and don't want to close loading indicator before refirect
  keepLoadingOnSuccess?: boolean;
}

export const EasyFormContext = React.createContext<IEasyFormBag>(undefined as any);
EasyFormContext.displayName = 'EasyFormContext';

export const EasyFormProvider = EasyFormContext.Provider;
export const EasyFormConsumer = EasyFormContext.Consumer;

/**
 * EasyForm
 *
 * Wraps a large chunk of the common logic and responsibilities that have been copied as boiler plate in many of our forms.
 * These responsibilities include:
 *  - Wrapping submission methods in loading setters
 *  - Parsing and exposing human readable error messages from server request
 *  - Displaying error messages
 *  - Combining various booleans to determine whether the submit button or inputs fields should be disabled
 *
 * Important to know:
 *  - There MUST me a Formik component wrapping this component
 *  - If there is an error catching statement in your form submission method, you must raise it if you want EasyForm to display the resulting errors
 * @param children
 * @param keepLoadingOnSuccess: Does not close loading after form submission completes. We use this in cases where we want to redirect upon success
 * @constructor
 */
const EasyForm: FunctionComponent<IProps> = ({ children, keepLoadingOnSuccess = false }) => {
  // This is dependent on <Formik> component parent for right now
  const { isSubmitting, setSubmitting, submitForm, isValid } = useFormikContext();

  const [errors, setErrors] = useState<ValidationErrorMessages>(null);
  const { loading, setLoading } = useLoadingContext();
  const submitDisabled = !isValid || isSubmitting || loading;
  const inputDisabled = isSubmitting || loading;

  const onClickSubmit = async (e) => {
    // May need to do something like this: https://github.com/jaredpalmer/formik/blob/main/packages/formik/src/Formik.tsx#L804
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setSubmitting(true);
    try {
      // We could instead use handleSubmit if we pull Formik into easy form and wrap onSubmit before passing to Formik
      await submitForm();
      if (!keepLoadingOnSuccess) {
        setLoading(false);
      }
    } catch (err) {
      // Report the error if there was a client-side error in the submission handler
      if (!(err.status && err.type && err.type === 'API_ERROR')) {
        Reporty.error(err);
      }
      setErrors(parseErrorMessagesFromResponse(err));
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  const easyFormBag = {
    onClickSubmit,
    submitDisabled,
    inputDisabled,
  };

  return (
    <FormContainer>
      <FormErrorDisplay errors={errors} />
      <Form>
        <EasyFormProvider value={easyFormBag}>{children(easyFormBag)}</EasyFormProvider>
      </Form>
    </FormContainer>
  );
};

export default EasyForm;
